import { FC, useState } from 'react';

import {
  FollowUpTemplateDataFragment,
  useFollowUpTemplateLazyQuery,
  useFollowUpTemplatesForPickerQuery,
} from '../../../generated/graphql';
import Button from '../../components/Button';
import ModalDialog from '../../components/ModalDialog';
import Spinner from '../../svgs/Spinner';
import FollowUpTemplateCard from '../FollowUpBuilder/FollowUpDrawer/FollowUpTemplateCard';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  stepIndex: number;
  onApplyTemplate: (template: FollowUpTemplateDataFragment) => void;
}

const ApplyTemplateDrawer: FC<Props> = ({
  isOpen,
  onClose,
  stepIndex,
  onApplyTemplate,
}) => {
  const { data, loading } = useFollowUpTemplatesForPickerQuery({
    variables: { input: { isDraft: false } },
    fetchPolicy: 'cache-first',
  });

  const [loadTemplate] = useFollowUpTemplateLazyQuery();

  const templates = (data?.followUpTemplates ?? []).filter((t) => !t.isDraft);
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(
    null,
  );

  const handleSelect = async (templateId: string) => {
    setPendingTemplateId(templateId);
    try {
      const result = await loadTemplate({
        variables: { followUpTemplateId: templateId },
      });
      if (result.data?.followUpTemplate) {
        onApplyTemplate(result.data.followUpTemplate);
        onClose();
      }
    } finally {
      setPendingTemplateId(null);
    }
  };

  return (
    <ModalDialog
      isOpen={isOpen}
      setClosed={onClose}
      width="large"
      overflow="auto"
      fullHeight
      title={`Apply Mail template to Mail #${stepIndex + 1}`}
      leftFooter={
        <Button
          title="Cancel"
          theme="secondary"
          size="medium"
          onClick={onClose}
        />
      }
      bodyClassName="min-h-0 !pb-12"
    >
      <div className="px-3">
        {loading ? (
          <Spinner className="mx-auto my-8" />
        ) : templates.length === 0 ? (
          <p className="text-small-caption text-neutral-110">
            No saved templates yet. Create one in the Mail composer.
          </p>
        ) : (
          <ul className="grid grid-cols-3 gap-4 pb-4">
            {templates.map((template) => (
              <li key={template.id} className="min-w-0">
                <FollowUpTemplateCard
                  followUpTemplate={template}
                  className="h-full"
                  showActions={false}
                  disabled={Boolean(pendingTemplateId)}
                  isApplying={pendingTemplateId === template.id}
                  onApplyFollowUpTemplate={async () => {
                    await handleSelect(template.id);
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalDialog>
  );
};

export default ApplyTemplateDrawer;
